import { TipoFrequencia, UnidadeMedidaTempoEnum } from 'graphql/types.generated'
import { round } from 'util/lang'
import { parseFraction, parseNumber } from 'util/number'
import { composeRules, ErrorObject, range, required } from 'util/validation'
import { isEmpty } from 'util/validation/Util'

import { MedicamentoFormModel, TipoDoseEnum } from '../model-prescricao'
import { validateDuracaoDentroDosLimitesPrescricao, validateFieldFracionado } from './prescricao-rules'
import { isCampoInvalido } from './utils-prescricao'

type ValidationFrequenciaStrategy = (values: MedicamentoFormModel, errors: ErrorObject<MedicamentoFormModel>) => void

const validateDoseUnica: ValidationFrequenciaStrategy = (values, errors) => {
  errors.intervaloDose = isCampoInvalido(!isEmpty(values.intervaloDose))(values.intervaloDose)
  errors.frequenciaDose = isCampoInvalido(!isEmpty(values.frequenciaDose))(values.frequenciaDose)

  errors.unidadeMedidaTempoFrequenciaTurno = isCampoInvalido(!isEmpty(values.unidadeMedidaTempoFrequenciaTurno))(
    values.unidadeMedidaTempoFrequenciaTurno
  )
  errors.quantidadePeriodoFrequenciaTurno = isCampoInvalido(!isEmpty(values.quantidadePeriodoFrequenciaTurno))(
    values.quantidadePeriodoFrequenciaTurno
  )

  const duracao = typeof values.duracao === 'string' ? parseNumber(values.duracao) : values.duracao

  errors.duracao = isCampoInvalido(duracao !== 1)(values.duracao)
  errors.escalaDuracao = isCampoInvalido(values.escalaDuracao !== UnidadeMedidaTempoEnum.DIAS)(values.escalaDuracao)
}

const validateIntervalo: ValidationFrequenciaStrategy = (values, errors) => {
  errors.intervaloDose = range(1, 99)(values.intervaloDose)

  validateDuracaoDentroDosLimitesPrescricao(values.intervaloDose, 'intervaloDose', true, values, errors)
}

const validateFrequencia: ValidationFrequenciaStrategy = (values, errors) => {
  errors.frequenciaDose = composeRules([required, range(1, 99)])(values.frequenciaDose)
  errors.quantidadePeriodoFrequenciaTurno = range(1, 99)(values.quantidadePeriodoFrequenciaTurno)

  validateDuracaoDentroDosLimitesPrescricao(
    values.quantidadePeriodoFrequenciaTurno,
    'quantidadePeriodoFrequenciaTurno',
    false,
    values,
    errors
  )
}

const validateTurno: ValidationFrequenciaStrategy = (values, errors) => {
  errors.turno = required(values.turno)
  errors.unidadeMedidaTempoFrequenciaTurno = required(values.unidadeMedidaTempoFrequenciaTurno)
  errors.quantidadePeriodoFrequenciaTurno = composeRules([required, range(1, 99)])(
    values.quantidadePeriodoFrequenciaTurno
  )

  validateDuracaoDentroDosLimitesPrescricao(
    values.quantidadePeriodoFrequenciaTurno,
    'quantidadePeriodoFrequenciaTurno',
    false,
    values,
    errors
  )
}

const validateFracionado: ValidationFrequenciaStrategy = (values, errors) => {
  errors.qtDoseManha = errors.qtDoseManha ?? validateFieldFracionado(values.qtDoseManha)
  errors.qtDoseTarde = errors.qtDoseTarde ?? validateFieldFracionado(values.qtDoseTarde)
  errors.qtDoseNoite = errors.qtDoseNoite ?? validateFieldFracionado(values.qtDoseNoite)
  errors.unidadeMedidaTempoFrequenciaTurno = required(values.unidadeMedidaTempoFrequenciaTurno)

  errors.quantidadePeriodoFrequenciaTurno = composeRules([required, range(1, 99)])(
    values.quantidadePeriodoFrequenciaTurno
  )

  const turnosPreenchidos = [!!values.qtDoseManha, !!values.qtDoseTarde, !!values.qtDoseNoite].filter(Boolean).length
  const totalDose = parseNumber(values.qtDose) ?? 0
  const totalDoseFracionada = round(
    (parseFraction(values.qtDoseManha) ?? 0) +
      (parseFraction(values.qtDoseTarde) ?? 0) +
      (parseFraction(values.qtDoseNoite) ?? 0),
    2
  )

  if (turnosPreenchidos < 2) {
    errors.turnosError = 'É necessário preencher pelo menos dois turnos.'
  }

  if (totalDose !== totalDoseFracionada) {
    errors.totalDoseError = `Quantidade total das doses fracionadas diferente da dose total. Dose total: ${totalDose} | Doses fracionadas: ${totalDoseFracionada}`
  }

  validateDuracaoDentroDosLimitesPrescricao(
    values.quantidadePeriodoFrequenciaTurno,
    'quantidadePeriodoFrequenciaTurno',
    false,
    values,
    errors
  )
}

export const getValidationFrequenciaStrategy = (values: MedicamentoFormModel): ValidationFrequenciaStrategy | null => {
  if (values.tipoDose === TipoDoseEnum.UNICA) return validateDoseUnica

  switch (values.tipoFrequencia) {
    case TipoFrequencia.INTERVALO:
      return validateIntervalo
    case TipoFrequencia.FREQUENCIA:
      return validateFrequencia
    case TipoFrequencia.TURNO:
      return validateTurno
    case TipoFrequencia.FRACIONADO:
      return validateFracionado
    default:
      return null
  }
}
